/**
 * ElementPalette - Draggable elements panel for adding new items to map
 */
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Tooltip,
  Divider,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ELEMENT_TYPES, ElementTypeConfig, ElementType } from './types';

interface ElementPaletteProps {
  onAddElement: (type: ElementType) => void;
  disabled?: boolean;
}

const ElementPalette: React.FC<ElementPaletteProps> = ({
  onAddElement,
  disabled = false,
}) => {
  const { t } = useTranslation();
  
  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    elementType: ElementTypeConfig
  ) => {
    e.dataTransfer.setData('elementType', elementType.type);
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {t('warehouseMap.elements')}
      </Typography>
      <Typography variant="caption" color="text.secondary" paragraph>
        {t('warehouseMap.elementsDescription')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        {ELEMENT_TYPES.map((elementType) => (
          <Tooltip
            key={elementType.type}
            title={t(`warehouseMap.elementDescriptions.${elementType.type}`)}
            placement="right"
            arrow
          >
            <motion.div
              whileHover={{ scale: disabled ? 1 : 1.05 }}
              whileTap={{ scale: disabled ? 1 : 0.95 }}
              style={{ width: '100%' }}
            >
              <Paper
                elevation={0}
                draggable={!disabled}
                onDragStart={(e) => handleDragStart(e, elementType)}
                onClick={() => !disabled && onAddElement(elementType.type)}
                sx={{
                  p: 1,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 1,
                  cursor: disabled ? 'not-allowed' : 'grab',
                  backgroundColor: elementType.defaultColor,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  transition: 'all 0.2s ease',
                  opacity: disabled ? 0.5 : 1,
                  width: '100%',
                  minHeight: 50,
                  overflow: 'hidden',
                  '&:hover': disabled ? {} : {
                    borderColor: 'primary.main',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  },
                  '&:active': disabled ? {} : {
                    cursor: 'grabbing',
                  },
                }}
              >
                <Typography fontSize={20} sx={{ flexShrink: 0 }}>{elementType.icon}</Typography>
                <Typography
                  variant="caption"
                  fontWeight={500}
                  textAlign="left"
                  sx={{
                    lineHeight: 1.2,
                    color: '#000000',
                    fontSize: '0.75rem',
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    flex: 1,
                  }}
                >
                  {t(`warehouseMap.elementTypes.${elementType.type}`)}
                </Typography>
              </Paper>
            </motion.div>
          </Tooltip>
        ))}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="caption" color="text.secondary">
        {t('warehouseMap.tip')}
      </Typography>
    </Paper>
  );
};

export default ElementPalette;

